import {useCallback, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {runAccountIdsSearch} from 'src/openapi/admin/apis/RunAccountSearchApi';
import {runUserIdsSearch} from 'src/openapi/admin/apis/UserSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {mcpLimitLogStateActions} from '../slices/mcp-limit-log-state.slice';

type UseEmailSearchProps = {
    accountId: number,
};

export const useEmailSearch = ({accountId}: UseEmailSearchProps) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const lastSearchQueryRef = useRef<string>('');

    const handleEmailSearch = useCallback(
        async (value: string, currentFilters: Record<string, unknown>) => {
            const trimmed = value.trim();
            if (lastSearchQueryRef.current === trimmed) {
                return;
            }
            lastSearchQueryRef.current = trimmed;

            if (trimmed) {
                try {
                    const [userSearchResult, accountSearchResult] = await Promise.allSettled([
                        apiDispatch(
                            runUserIdsSearch({
                                userIdsSearchRequest: {
                                    searchCriteria: {
                                        accountIds: [accountId],
                                        userEmails: [trimmed],
                                    },
                                },
                            }),
                        ),
                        apiDispatch(
                            runAccountIdsSearch({
                                accountIdsSearchRequest: {
                                    searchCriteria: {
                                        accountNames: [trimmed],
                                    },
                                },
                            }),
                        ),
                    ]);

                    const foundUserIds =
                        userSearchResult.status === 'fulfilled' &&
                        userSearchResult.value.payload?.success
                            ? (userSearchResult.value.payload.data as number[] | undefined) || []
                            : [];

                    const foundAccountIds =
                        accountSearchResult.status === 'fulfilled' &&
                        accountSearchResult.value.payload?.success
                            ? (accountSearchResult.value.payload.data as number[] | undefined) || []
                            : [];

                    const hasResults = foundUserIds.length > 0 || foundAccountIds.length > 0;

                    dispatch(
                        mcpLimitLogStateActions.setFilters({
                            ...currentFilters,
                            accountEmail: trimmed,
                            userIds: hasResults ? foundUserIds : [-1],
                            accountIds: hasResults ? foundAccountIds : [-1],
                        }),
                    );
                } catch {
                    dispatch(
                        mcpLimitLogStateActions.setFilters({
                            ...currentFilters,
                            accountEmail: trimmed,
                            userIds: [-1],
                            accountIds: [-1],
                        }),
                    );
                }
            } else {
                const {accountEmail, userIds, accountIds, ...rest} = currentFilters;
                dispatch(mcpLimitLogStateActions.setFilters(rest));
            }
        },
        [accountId, apiDispatch, dispatch],
    );

    const clearEmailSearch = useCallback(
        (currentFilters: Record<string, unknown>) => {
            lastSearchQueryRef.current = '';
            const {accountEmail, userIds, accountIds, ...rest} = currentFilters;
            dispatch(mcpLimitLogStateActions.setFilters(rest));
        },
        [dispatch],
    );

    return {handleEmailSearch, clearEmailSearch};
};
